import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn } from "@fortawesome/pro-solid-svg-icons";
import type { TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

const ON_COLOR = "#31aba6";
const OFF_COLOR = "grey";

/** A tiny observable set of the row keys currently on the chart, shared with the grid's icons. */
const createSelectionStore = () => {
    let keys = new Set<string>();
    const listeners = new Set<() => void>();
    return {
        has: (key: string) => keys.has(key),
        set: (next: Set<string>) => {
            keys = next;
            listeners.forEach((listener) => listener());
        },
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

type SelectionStore = ReturnType<typeof createSelectionStore>;

// React 17: no useSyncExternalStore, so subscribe by hand. Re-reading on subscribe closes the gap
// between the first render and the effect.
const ChartToggleIcon = ({ store, rowKey }: { store: SelectionStore; rowKey: string }) => {
    const [on, setOn] = useState(() => store.has(rowKey));
    useEffect(() => {
        const sync = () => setOn(store.has(rowKey));
        sync();
        return store.subscribe(sync);
    }, [store, rowKey]);
    return <FontAwesomeIcon icon={faChartColumn} height={25} color={on ? ON_COLOR : OFF_COLOR} />;
};

/**
 * Add-or-remove chart lines from a DataGrid "Show on Chart" button.
 *
 * Why this exists: `DataGrid` builds its cell renderers once, from the first data, so the `button`
 * a cell holds is the one from that render. A handler that reads `series` from its closure therefore
 * always sees the initial empty list: every click appended another copy of the line in a new color,
 * and the icon (which read `button.series`) never changed. Here the handler and the icon read refs
 * and a small store instead of render-time state, so they stay correct however stale the grid's
 * copy of the button is.
 *
 * `toggle(key, load)` adds or removes a row's lines on a click; `ensure(key, load)` adds them
 * without a click (a row a page puts on the chart by default). For both, `key` identifies the row (use a stable id, e.g. the peer's trading item id);
 * `load` fetches the line(s) that row adds. Every series `load` returns is removed with its row.
 * Clicks on a row whose lines are still loading are ignored. Everything resets when `resetKey`
 * changes (a new stock), and a load that finishes after a reset is dropped.
 */
export const useChartSeriesToggle = (resetKey: unknown) => {
    const [series, setSeries] = useState<TimeSeries[]>([]);
    const seriesRef = useRef<TimeSeries[]>([]);
    const groupsRef = useRef<Map<string, string[]>>(new Map());
    const pendingRef = useRef<Set<string>>(new Set());
    const generationRef = useRef(0);
    const store = useMemo(createSelectionStore, []);

    const commit = useCallback(
        (next: TimeSeries[]) => {
            seriesRef.current = next;
            setSeries(next);
            store.set(new Set(groupsRef.current.keys()));
        },
        [store]
    );

    useEffect(() => {
        generationRef.current += 1;
        groupsRef.current = new Map();
        pendingRef.current = new Set();
        commit([]);
    }, [resetKey, commit]);

    const add = useCallback(
        async (key: string, load: () => Promise<TimeSeries[]>) => {
            if (groupsRef.current.has(key) || pendingRef.current.has(key)) return;
            pendingRef.current.add(key);
            const generation = generationRef.current;
            try {
                const loaded = await load();
                if (generation !== generationRef.current || groupsRef.current.has(key) || !loaded.length) return;
                const ids = loaded.map((serie) => serie.id);
                groupsRef.current.set(key, ids);
                commit([...seriesRef.current.filter((serie) => !ids.includes(serie.id)), ...loaded]);
            } catch (err) {
                console.error("[useChartSeriesToggle] Could not add the series to the chart:", err);
            } finally {
                if (generation === generationRef.current) pendingRef.current.delete(key);
            }
        },
        [commit]
    );

    const toggle = useCallback(
        async (key: string, load: () => Promise<TimeSeries[]>, onRemove?: () => void) => {
            const added = groupsRef.current.get(key);
            if (added) {
                groupsRef.current.delete(key);
                commit(seriesRef.current.filter((serie) => !added.includes(serie.id)));
                onRemove?.();
                return;
            }
            return add(key, load);
        },
        [add, commit]
    );

    /**
     * Put a row's lines on the chart without the user clicking, e.g. a page that shows its closest
     * peer by default. A no-op when the row is already on the chart or loading, so a page can call
     * it for none, one or several rows; the row's button then shows it as on, and a click removes it.
     */
    const ensure = add;

    /** Per-row icon that follows the chart's state, for `button.renderIcon`. */
    const renderIcon = useCallback((rowKey: string) => <ChartToggleIcon store={store} rowKey={rowKey} />, [store]);

    return { series, toggle, ensure, renderIcon };
};
